import { getProcessInstances } from '@/api/backend/workflow';
import { ProcessInstanceResponseType } from '@/api/backend/types';
import { ProcessStatusBadge } from '@/components/process-status-badge';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { Button, Card, DataTable, DataTableColumnType, Link } from '@opaz/ui-components';
import { useQuery } from '@tanstack/react-query';

import { useScrollToSection } from '@/hooks/use-scroll-to-section';
import { DASHBOARD_SECTIONS } from '@/lib/dashboard-sections';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

export function ActiveProcessesCard() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  useScrollToSection(DASHBOARD_SECTIONS.activeApplications);

  const { data: page, isLoading: isApplicationsLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: () => {
      return getProcessInstances(
        {
          start: 0,
          size: 5,
        },
        null,
        { active: true },
      );
    },
  });

  const columns: DataTableColumnType<ProcessInstanceResponseType>[] = [
    {
      title: t('dashboard.applications.headings.id'),
      render: (record: ProcessInstanceResponseType) => (
        <Link
          onClick={() => {
            navigate({
              to: `/processes/${record.id}`,
              params: { processInstanceId: record.id },
            });
          }}
          className="w-[120px] text-start"
        >
          {record.businessKey}
        </Link>
      ),
    },
    {
      title: t('dashboard.applications.headings.applicationName'),
      render: (record: ProcessInstanceResponseType) => {
        const zoneCode = record.context?.zoneCode ?? record.parent?.context?.zoneCode ?? record.root?.context?.zoneCode;
        return (
          <div className="flex flex-col gap-1">
            <div className="text-[#132459] font-bold text-base">
              {record.translations?.[i18n.language]?.processDefinitionName ?? record.processDefinitionName}
            </div>
            <span>{zoneCode ? t(`common.zoneCode.${zoneCode}`) : t('common.unknown')}</span>
          </div>
        );
      },
    },
    {
      title: t('dashboard.applications.headings.status'),
      align: 'end',
      render: (record: ProcessInstanceResponseType) => {
        return (
          <div className="flex flex-col items-end gap-1">
            <ProcessStatusBadge businessStatus={record.businessStatus} completed={record.completed} />
          </div>
        );
      },
    },
  ];

  return (
    <Card id={DASHBOARD_SECTIONS.activeApplications} className="w-full scroll-mt-24 p-5 shadow-lg border-none">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-semibold">{t('dashboard.activeApplications.title')}</h2>
        <Button
          variant="secondary"
          onClick={() =>
            navigate({
              to: '/processes',
            })
          }
        >
          {t('dashboard.activeApplications.allApplications')}
        </Button>
      </div>

      <ResponsiveDataView
        columns={columns}
        data={page?.data?.length ? [...page.data] : []}
        isLoading={isApplicationsLoading}
        emptyMessage={t('common.table.emptyState')}
        getRowKey={(record) => record.id}
      >
        <DataTable
          pagination={false}
          columns={columns}
          dataSource={page?.data?.length ? [...page.data] : []}
          isLoading={isApplicationsLoading}
          translations={{
            noData: t('common.table.emptyState'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
}
